import { StatusDataResponse } from '../common/common-contracts';

type ApiResponse<Resp, Err = unknown> =
  | SuccessApiResponse<Resp>
  | ServiceErrorApiResponse<Err>
  | NetworkErrorApiResponse
  | InternalErrorApiResponse;

interface SuccessApiResponse<T> {
  status: ResponseStatus.SUCCESS;
  result: T;
}

type ResponseType = 'blob' | 'text';

interface ServiceErrorApiResponse<ErrorData = unknown> {
  status: ResponseStatus.SERVICE_ERROR;
  error: {
    errorMessage: string;
    errorData: ErrorData;
    httpStatusCode: ResponseHttpStatusCode;
    headers?: Record<string, string>;
  };
}

interface StatusRefreshResponse<DataType> {
  data: DataType;
  status: StatusDataResponse;
}

interface NetworkErrorApiResponse {
  status: ResponseStatus.NETWORK_ERROR;
}

interface InternalErrorApiResponse {
  status: ResponseStatus.INTERNAL_ERROR;
  error: unknown;
}

enum ResponseStatus {
  SUCCESS,
  SERVICE_ERROR,
  NETWORK_ERROR,
  INTERNAL_ERROR,
}

enum ResponseHttpStatusCode {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  PAYLOAD_TOO_LARGE = 413,
  LOCKED = 423,
}

enum ApiClientMode {
  SHARD,
  SUPREME,
  PHONE_BUS,
  CHATBOT_ASSISTANT,
}

type ContentType = 'multipart/form-data' | 'application/json';

type HttpRequestHeaders = Record<string, string | number | boolean>;

interface HttpRequestConfig {
  headers?: HttpRequestHeaders;
  params?: unknown;
  responseType?: ResponseType;
}

interface IHttpClient {
  get<RESP, ERR = unknown>(url: Url, config?: HttpRequestConfig): Promise<ApiResponse<RESP, ERR>>;

  post<REQ, RESP, ERR = unknown>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>>;

  put<REQ, RESP, ERR = unknown>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>>;

  delete<RESP, REQ = unknown, ERR = unknown>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>>;
}

interface IApiClient {
  httpClient: IHttpClient;
  get<RESP, PARAMS, ERR = unknown>(
    path: UrlPath,
    params?: PARAMS,
    mode?: ApiClientMode,
    responseType?: ResponseType,
  ): Promise<ApiResponse<RESP, ERR>>;

  post<REQ, RESP, ERR = unknown>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
    contentType?: ContentType,
    responseType?: ResponseType,
  ): Promise<ApiResponse<RESP, ERR>>;

  put<REQ, RESP, ERR = unknown>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<RESP, ERR>>;

  delete<RESP, REQ = unknown, ERR = unknown>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<RESP, ERR>>;
}

interface IHttpClientWithHeaders extends IHttpClient {
  getWithHeaders<RESP>(
    url: Url,
    config?: HttpRequestConfig,
  ): Promise<{ data: RESP; headers: Record<string, string> }>;
  postWithHeaders<REQ, RESP>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<{ data: RESP; headers: Record<string, string> }>;
}

interface IFileDownloadApiClient extends IApiClient {
  httpClient: IHttpClientWithHeaders;
  downloadFile<PARAMS = undefined>(
    path: UrlPath,
    params?: PARAMS,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<{ blob: Blob; filename: FileName }>>;
  downloadFileByPost<REQ>(
    path: UrlPath,
    data?: REQ,
    mode?: ApiClientMode,
  ): Promise<ApiResponse<{ blob: Blob; filename: FileName }>>;
}

type RealTimeMessage = string | ArrayBufferLike | Blob | ArrayBufferView;

interface IRealtimeClient {
  openConnection(
    url?: WsUrl,
    onOpen?: PureCallback,
    onClose?: PureCallback,
    onError?: PureCallback,
  ): Promise<boolean>;
  closeConnection(): void;
  isStarted(): boolean;
  send<MessageType extends RealTimeMessage>(message: MessageType): void;
  setHandler<ResponseType>(handler: (response: ResponseType) => void);
  state: RealtimeClientState | undefined;
}

enum RealtimeClientState {
  CONNECTING,
  OPEN,
  CLOSING,
  CLOSED,
}

export {
  type SuccessApiResponse,
  type ServiceErrorApiResponse,
  type StatusRefreshResponse,
  type NetworkErrorApiResponse,
  type InternalErrorApiResponse,
  type ApiResponse,
  type ResponseType,
  type ContentType,
  type IHttpClient,
  type IHttpClientWithHeaders,
  type IApiClient,
  type IFileDownloadApiClient,
  type HttpRequestConfig,
  type RealTimeMessage,
  type IRealtimeClient,
  ResponseStatus,
  ResponseHttpStatusCode,
  ApiClientMode,
  RealtimeClientState,
};
